import { inject, Injectable } from '@angular/core';
import { NbAuthStrategy, NbAuthResult, NbAuthSimpleToken } from '@nebular/auth';
import {
  Auth,
  createUserWithEmailAndPassword,
  deleteUser,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
} from '@angular/fire/auth';
import {
  doc,
  Firestore,
  getDoc,
  runTransaction,
  serverTimestamp,
  setDoc,
} from '@angular/fire/firestore';
import { NbToastrService } from '@nebular/theme';
import { from, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  allowedEmailDomainsLabel,
  isAllowedCompanyEmail,
} from '../utils/company-details';
import { PortusRouteReuseStrategy } from '../pages/route-reuse.strategy';

export interface FirebaseAuthStrategyOptions {
  name: string;
  token: {
    class: typeof NbAuthSimpleToken;
  };
}

@Injectable()
export class FirebaseAuthStrategy extends NbAuthStrategy {
  firestore = inject(Firestore);

  defaultOptions: FirebaseAuthStrategyOptions = {
    name: 'email',
    token: {
      class: NbAuthSimpleToken,
    },
  };

  constructor(
    private afAuth: Auth,
    private toastrService: NbToastrService,
  ) {
    super();
  }

  static setup(options: FirebaseAuthStrategyOptions): [any, any] {
    return [FirebaseAuthStrategy, options];
  }

register(data?: any): Observable<NbAuthResult> {
  return from(
    (async () => {
      const email = String(data?.email || '')
        .trim()
        .toLowerCase();

      const password = String(data?.password || '');
      const fullName = String(data?.fullName || data?.name || '').trim();

      if (!email || !password) {
        throw new Error('Email and password are required.');
      }

      if (!fullName) {
        throw new Error('Full name is required.');
      }

      if (!isAllowedCompanyEmail(email)) {
        throw new Error(
          `Only official company emails are allowed: ${allowedEmailDomainsLabel()}.`,
        );
      }

      const userCredential = await createUserWithEmailAndPassword(
        this.afAuth,
        email,
        password,
      );

      const uid = userCredential.user.uid;
      const userDocRef = doc(this.firestore, 'users', uid);
      const bootstrapDocRef = doc(
        this.firestore,
        'system',
        'appBootstrap',
      );

      try {
        const registrationResult = await runTransaction(
          this.firestore,
          async transaction => {
            const bootstrapSnapshot = await transaction.get(bootstrapDocRef);

            /*
             * If appBootstrap does not exist, this is the first registered user.
             * Firestore transactions ensure only one user can become Super Admin.
             */
            const isFirstUser = !bootstrapSnapshot.exists();

            const role = isFirstUser ? 'superadmin' : 'user';
            const status = isFirstUser ? 'approved' : 'notApproved';
            const permissions = isFirstUser
              ? { all: true }
              : {};

            transaction.set(userDocRef, {
              uid,
              name: fullName,
              email,
              role,
              status,
              permissions,
              profileImage: '',
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            });

            if (isFirstUser) {
              transaction.set(bootstrapDocRef, {
                initialized: true,
                superAdminUid: uid,
                superAdminEmail: email,
                createdAt: serverTimestamp(),
              });
            }

            return {
              uid,
              email,
              fullName,
              role,
              status,
              permissions,
              isFirstUser,
            };
          },
        );

        if (!registrationResult.isFirstUser) {
          await signOut(this.afAuth).catch(() => undefined);
        }

        return registrationResult;
      } catch (error) {
        /*
         * Remove the Firebase Auth account if Firestore registration fails.
         * This prevents orphan Auth accounts.
         */
        await deleteUser(userCredential.user).catch(() => undefined);
        await signOut(this.afAuth).catch(() => undefined);

        throw error;
      }
    })(),
  ).pipe(
    map((result: any) => {
      if (result.isFirstUser) {
        this.clearWorkspaceStorage();

        localStorage.setItem(
          'userData',
          JSON.stringify({
            uid: result.uid,
            name: result.fullName,
            email: result.email,
            role: result.role,
            status: result.status,
            permissions: result.permissions,
            profileImage: '',
          }),
        );

        this.toastrService.success(
          'First account created and approved as Super Admin.',
          'Super Admin Created',
        );

        return new NbAuthResult(
          true,
          null,
          '/auth/upload-profile-popup',
          [],
          'SUPERADMIN_CREATED',
          new NbAuthSimpleToken(
            result.uid,
            this.getOption('name'),
          ),
        );
      }

      this.toastrService.warning(
        'Your account has been created. Please wait for administrator approval.',
        'Pending Approval',
      );

      return new NbAuthResult(
        false,
        null,
        null,
        [],
        'Pending approval',
      );
    }),

    catchError((err) => {
      const rawMessage = err?.message || 'Registration failed.';

      const isMissingFirestore =
        err?.code === 'not-found' ||
        rawMessage
          .toLowerCase()
          .includes('database (default) does not exist');

      const message = isMissingFirestore
        ? 'Firestore database is not created for this Firebase project.'
        : rawMessage;

      this.toastrService.danger(
        message,
        'Registration Failed',
      );

      return of(
        new NbAuthResult(
          false,
          null,
          null,
          [message],
          'Registration failed',
        ),
      );
    }),
  );
}

  authenticate(data?: any): Observable<NbAuthResult> {
    return from(
      (async () => {
        const email = String(data?.email || '')
          .trim()
          .toLowerCase();
        const password = String(data?.password || '');

        if (!email || !password) {
          throw new Error('Email and password are required.');
        }

        if (!isAllowedCompanyEmail(email)) {
          throw new Error(
            `Only official company emails are allowed: ${allowedEmailDomainsLabel()}.`,
          );
        }

        const userCredential = await signInWithEmailAndPassword(
          this.afAuth,
          email,
          password,
        );

        const uid = userCredential.user?.uid || '';
        const userEmail = String(userCredential.user?.email || email)
          .trim()
          .toLowerCase();

        if (!uid) {
          await signOut(this.afAuth).catch(() => undefined);
          throw new Error('User UID not found.');
        }

        const userDocRef = doc(this.firestore, 'users', uid);
        const snap = await getDoc(userDocRef);

        if (!snap.exists()) {
          await signOut(this.afAuth).catch(() => undefined);
          throw new Error('User record not found.');
        }

        const userData: any = snap.data();
        const storedEmail = String(userData?.email || '')
          .trim()
          .toLowerCase();
        const status = String(userData?.status || '')
          .trim()
          .toLowerCase();

        if (!isAllowedCompanyEmail(userEmail) || !isAllowedCompanyEmail(storedEmail)) {
          await signOut(this.afAuth).catch(() => undefined);
          throw new Error('This email domain is not allowed.');
        }

        if (storedEmail && storedEmail !== userEmail) {
          await signOut(this.afAuth).catch(() => undefined);
          throw new Error('Authentication email does not match user record.');
        }

        if (status !== 'approved') {
          await signOut(this.afAuth).catch(() => undefined);
          throw new Error('Your account is not approved yet.');
        }

        const profileImage = userData?.profileImage || '';

        if (!profileImage) {
          return {
            forceUpload: true,
            uid,
            userData,
          };
        }

        return {
          forceUpload: false,
          userData,
          uid,
        };
      })(),
    ).pipe(
      map((result: any) => {
        if (result.forceUpload) {
          this.clearWorkspaceStorage();

          return new NbAuthResult(
            true,
            null,
            '/auth/upload-profile-popup',
            [],
            'UPLOAD_REQUIRED',
            new NbAuthSimpleToken(result.uid, this.getOption('name')),
          );
        }

        /*
         * A successful login always starts in the Welcome workspace.
         * Normal browser refreshes do not execute this authentication block,
         * so valid open tabs still restore during the active session.
         */
        this.clearWorkspaceStorage();
        localStorage.setItem('userData', JSON.stringify({ uid: result.uid, ...result.userData }));

        setTimeout(() => {
          window.location.href = '/';
        }, 100);

        return new NbAuthResult(
          true,
          null,
          '/',
          [],
          'Login successful',
          new NbAuthSimpleToken(result.uid, this.getOption('name')),
        );
      }),
      catchError((err) => {
        this.toastrService.danger(err.message, 'Login Failed');

        return of(
          new NbAuthResult(
            false,
            null,
            null,
            [err.message],
            'Login failed',
          ),
        );
      }),
    );
  }

  private clearWorkspaceStorage(): void {
    const exactKeys = [
      'portus_mylogistics_workspace_tabs',
      'portus_mylogistics_workspace_active_tab',
      'currentFormKey',
      'quotationFormDraft',
    ];

    const keyPrefixes = [
      'portus_mylogistics_workspace_draft::',
      'portus_mylogistics_closed_draft::',
      'portus_mylogistics_job_draft::',
      'portus_mylogistics_export_job_draft::',
      'portus_mylogistics_dynamic_form_draft::',
      'portus_mylogistics_quotation_draft::',
    ];

    const keysToRemove: string[] = [];

    for (let index = 0; index < localStorage.length; index++) {
      const key = localStorage.key(index);

      if (!key) {
        continue;
      }

      const shouldRemove =
        exactKeys.includes(key) ||
        keyPrefixes.some((prefix) => key.startsWith(prefix));

      if (shouldRemove) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => {
      localStorage.removeItem(key);
    });

    sessionStorage.removeItem('portus_mylogistics_workspace_tabs');
    sessionStorage.removeItem('portus_mylogistics_workspace_active_tab');
  }

  logout(): Observable<NbAuthResult> {
    return from(signOut(this.afAuth)).pipe(
      map(() => {
        PortusRouteReuseStrategy.clearAll();
        this.clearWorkspaceStorage();

        return new NbAuthResult(
          true,
          null,
          '/',
          [],
          'Logout successful',
        );
      }),
      catchError((err) =>
        of(
          new NbAuthResult(
            false,
            null,
            '/',
            [err?.message || 'Logout failed'],
            'Logout failed',
          ),
        ),
      ),
    );
  }

  refreshToken(data?: any): Observable<NbAuthResult> {
    return of(new NbAuthResult(true));
  }

  requestPassword(data?: any): Observable<NbAuthResult> {
    return from(
      (async () => {
        const email = String(data || '')
          .trim()
          .toLowerCase();

        if (!email) {
          throw new Error('Email is required');
        }

        if (!isAllowedCompanyEmail(email)) {
          throw new Error(
            `Only official company emails are allowed: ${allowedEmailDomainsLabel()}.`,
          );
        }

        await sendPasswordResetEmail(this.afAuth, email);

        return { email };
      })(),
    ).pipe(
      map(({ email }) => {
        this.toastrService.success(
          `Password reset link sent to ${email}`,
          'Success',
        );

        return new NbAuthResult(
          true,
          null,
          '/',
          [],
          'Password reset email sent',
        );
      }),
      catchError((err) => {
        this.toastrService.danger(err.message, 'Password Reset Failed');

        return of(
          new NbAuthResult(
            false,
            null,
            '/',
            [err.message],
            'Password reset failed',
          ),
        );
      }),
    );
  }

  resetPassword(data?: any): Observable<NbAuthResult> {
    return of(
      new NbAuthResult(
        true,
        null,
        '/',
        [],
        'Password reset process started',
      ),
    );
  }
}
